import { useSyncExternalStore } from 'react';

export interface CartItem {
  productId: string;
  name: string;
  slug: string;
  price: number;
  image: string;
  volume: string;
  quantity: number;
}

interface CartState {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
}

let state: CartState = loadFromStorage();
const listeners = new Set<() => void>();

function loadFromStorage(): CartState {
  if (typeof window === 'undefined') return { items: [], totalItems: 0, totalPrice: 0 };
  try {
    const saved = localStorage.getItem('qoqo-cart');
    const items: CartItem[] = saved ? JSON.parse(saved) : [];
    return computeState(items);
  } catch {
    return { items: [], totalItems: 0, totalPrice: 0 };
  }
}

function saveToStorage(items: CartItem[]) {
  localStorage.setItem('qoqo-cart', JSON.stringify(items));
}

function computeState(items: CartItem[]): CartState {
  return {
    items,
    totalItems: items.reduce((sum, i) => sum + i.quantity, 0),
    totalPrice: items.reduce((sum, i) => sum + i.price * i.quantity, 0),
  };
}

function notify() {
  listeners.forEach((cb) => cb());
}

function setItems(nextItems: CartItem[]) {
  saveToStorage(nextItems);
  state = computeState(nextItems);
  notify();
}

export function addItem(item: Omit<CartItem, 'quantity'>, quantity = 1) {
  const prev = state.items;
  const match = prev.find((i) => i.productId === item.productId);
  if (match) {
    const next = prev.map((i) =>
      i.productId === item.productId ? { ...i, quantity: i.quantity + quantity } : i
    );
    setItems(next);
  } else {
    setItems([...prev, { ...item, quantity }]);
  }
}

export function removeItem(productId: string) {
  setItems(state.items.filter((i) => i.productId !== productId));
}

export function updateQuantity(productId: string, quantity: number) {
  if (quantity <= 0) {
    setItems(state.items.filter((i) => i.productId !== productId));
    return;
  }
  setItems(state.items.map((i) => (i.productId === productId ? { ...i, quantity } : i)));
}

export function clearCart() {
  setItems([]);
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getSnapshot() {
  return state;
}

export function useCart() {
  return useSyncExternalStore(subscribe, getSnapshot);
}